// pages/admin/AdminLoyalty.jsx — réglages du programme de fidélité, propre à
// chaque tenant. Le suivi se fait par JOUEUR (fiche client), pas seulement
// par contact de réservation ; la récompense (place offerte / réduction) se
// déclenche et s'applique automatiquement, sans validation manuelle du staff.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/auth';
import { loyaltyApi } from '../../utils/api';
import { Gift, History } from 'lucide-react';
import toast from 'react-hot-toast';

const REWARD_TYPES = [
  { value: 'free_session',     label: 'Place / session offerte' },
  { value: 'percent_discount', label: 'Réduction en pourcentage' },
  { value: 'fixed_discount',   label: 'Réduction montant fixe' },
];

function eventLabel(type) {
  return {
    session_counted:  'Session comptabilisée',
    reward_earned:    'Récompense gagnée',
    reward_redeemed:  'Récompense utilisée',
    reward_expired:   'Récompense expirée',
    manual_adjustment:'Ajustement manuel',
  }[type] || type;
}

export default function AdminLoyalty() {
  const { can } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([loyaltyApi.getSettings(), loyaltyApi.events()])
      .then(([s, e]) => {
        const settings = s.data.settings;
        setForm({
          enabled: !!settings.enabled,
          sessionsRequired: settings.sessions_required ?? 5,
          rewardType: settings.reward_type || 'free_session',
          rewardValue: settings.reward_value ?? '',
          minPlayersForFree: settings.min_players_for_free ?? 4,
          maxRewardsPerBooking: settings.max_rewards_per_booking ?? 1,
          validityDays: settings.validity_days ?? '',
          allowStackingManualDiscount: !!settings.allow_stacking_manual_discount,
        });
        setEvents(e.data.events || []);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!can('admin')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await loyaltyApi.updateSettings({
        ...form,
        rewardValue: form.rewardValue === '' ? null : parseFloat(form.rewardValue),
        validityDays: form.validityDays === '' ? null : parseInt(form.validityDays, 10),
      });
      toast.success('Réglages fidélité enregistrés');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Programme de fidélité</h2>
          <p>Suivi par joueur, déclenchement et application automatiques</p>
        </div>
      </div>

      <div className="page-body" style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
        {loading || !form ? (
          <div style={{ textAlign: 'center', padding: 30 }}><span className="spinner" /></div>
        ) : (
          <form className="card" onSubmit={submit}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: form.enabled ? 'rgba(0,210,180,0.15)' : 'var(--bg-elevated)',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Gift size={16} color={form.enabled ? 'var(--teal)' : 'var(--text-3)'} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Activer le programme</h3>
                <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
                  Une fois activé, chaque session terminée compte automatiquement pour le client.
                </p>
              </div>
              <div onClick={() => set('enabled', !form.enabled)}
                style={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', flexShrink: 0,
                  background: form.enabled ? 'var(--teal)' : 'var(--bg-base)',
                  border: '1px solid var(--border)', position: 'relative', transition: 'background 0.2s' }}>
                <div style={{ position: 'absolute', top: 3, left: form.enabled ? 22 : 3,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Sessions requises avant récompense</label>
                <input type="number" min="1" className="input" value={form.sessionsRequired}
                  onChange={e => set('sessionsRequired', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Type de récompense</label>
                <select className="input" value={form.rewardType}
                  onChange={e => set('rewardType', e.target.value)}>
                  {REWARD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                {form.rewardType === 'percent_discount' ? 'Pourcentage de réduction (%)' : 'Montant de la réduction (€)'}
              </label>
              {form.rewardType === 'free_session' && (
                <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: -2, marginBottom: 6 }}>
                  Montant fixe déduit du total, indépendant des tarifs du groupe (pas une moyenne recalculée).
                </p>
              )}
              <input type="number" step="0.01" min="0" className="input" value={form.rewardValue}
                onChange={e => set('rewardValue', e.target.value)} placeholder="0" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Joueurs min. sur la partie pour en bénéficier</label>
                <input type="number" min="1" className="input" value={form.minPlayersForFree}
                  onChange={e => set('minPlayersForFree', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Récompenses max. appliquées / réservation</label>
                <input type="number" min="1" className="input" value={form.maxRewardsPerBooking}
                  onChange={e => set('maxRewardsPerBooking', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Validité de la récompense (jours, vide = illimité)</label>
              <input type="number" min="1" className="input" value={form.validityDays}
                onChange={e => set('validityDays', e.target.value)} placeholder="Pas d'expiration" />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, marginBottom: 4 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Cumul avec une remise manuelle/partenaire</div>
                <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
                  Si désactivé (par défaut), la récompense fidélité et une remise manuelle/partenaire
                  ne peuvent pas s'appliquer en même temps sur la même réservation.
                </p>
              </div>
              <div onClick={() => set('allowStackingManualDiscount', !form.allowStackingManualDiscount)}
                style={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', flexShrink: 0,
                  background: form.allowStackingManualDiscount ? 'var(--teal)' : 'var(--bg-base)',
                  border: '1px solid var(--border)', position: 'relative', transition: 'background 0.2s' }}>
                <div style={{ position: 'absolute', top: 3, left: form.allowStackingManualDiscount ? 22 : 3,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
              </div>
            </div>

            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <History size={15} />
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Événements récents</h3>
          </div>
          {events.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-3)' }}>Aucun événement pour le moment</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
              {events.map(ev => (
                <div key={ev.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10,
                  padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 8, fontSize: 12 }}>
                  <span>{eventLabel(ev.event_type)}{ev.booking_ref ? ` · ${ev.booking_ref}` : ''}</span>
                  <span style={{ color: 'var(--text-3)' }}>{new Date(ev.created_at).toLocaleString('fr-FR')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
